import React, { useState } from 'react'
import { Trash2, X, CheckCircle, AlertCircle, Loader2 } from 'lucide-react'
import { platformAPI } from '../services/api'

// Permanent purge of a tenant and everything under it (owner only). Moved out of the
// old tenants table, which no longer has row actions; opened from the workspace rail.
export const PurgeTenantModal = ({ tenant: tenantToDelete, onClose, onPurged }) => {
  const [deleteConfirmText, setDeleteConfirmText] = useState('')
  const [deleting, setDeleting] = useState(false)
  const [deleteResult, setDeleteResult] = useState(null)
  const [error, setError] = useState('')

  const closeDeleteModal = () => {
    if (deleting) return
    if (deleteResult) onPurged?.(deleteResult)
    onClose()
  }

  const confirmHardDelete = async () => {
    setDeleting(true)
    setError('')
    try {
      const resp = await platformAPI.hardPurgeTenantBySlug(tenantToDelete.slug)
      setDeleteResult(resp.data || { message: 'Estúdio excluído' })
    } catch (err) {
      const msg =
        err.response?.data?.error?.message ||
        err.response?.data?.message ||
        err.response?.data?.error ||
        err.message ||
        'Falha ao excluir o estúdio'
      setError(typeof msg === 'string' ? msg : 'Falha ao excluir o estúdio')
    } finally {
      setDeleting(false)
    }
  }

  if (!tenantToDelete) return null

  return (
    <div className="fixed inset-0 bg-[rgba(15,23,42,.45)] flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-palette max-w-lg w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-admin-200">
          <div className="flex items-center">
            <div className="p-2 bg-red-100 rounded-lg mr-3">
              <Trash2 className="h-5 w-5 text-red-600" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-admin-900">Excluir estúdio</h2>
              <p className="text-sm text-admin-600">Esta ação não pode ser desfeita.</p>
            </div>
          </div>
          <button
            onClick={closeDeleteModal}
            disabled={deleting}
            aria-label="Fechar"
            className="p-2 text-admin-400 hover:text-admin-600 rounded-lg transition-colors disabled:opacity-50"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {deleteResult ? (
          <div className="p-6 space-y-4">
            <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
              <div className="flex items-center">
                <CheckCircle className="h-5 w-5 text-green-600 mr-2" />
                <p className="text-green-800 font-medium">
                  Estúdio excluído com sucesso
                </p>
              </div>
              {deleteResult.deleted_counts && (
                <div className="mt-3 text-xs text-green-700 grid grid-cols-2 gap-x-4 gap-y-1">
                  {Object.entries(deleteResult.deleted_counts).map(([k, v]) => (
                    <div key={k} className="flex justify-between gap-2">
                      <span className="text-admin-600">{k}</span>
                      <span className="font-mono">{v}</span>
                    </div>
                  ))}
                </div>
              )}
              {deleteResult.s3_cleanup && (
                <p className="text-xs text-green-700 mt-2">
                  Objetos do S3 excluídos: {deleteResult.s3_cleanup.deleted ?? 0}
                  {deleteResult.s3_cleanup.errors?.length
                    ? ` (${deleteResult.s3_cleanup.errors.length} erros)`
                    : ''}
                </p>
              )}
            </div>
            <div className="flex items-center justify-end pt-2 border-t border-admin-200">
              <button type="button" onClick={closeDeleteModal} className="btn btn-primary">
                Concluir
              </button>
            </div>
          </div>
        ) : (
          <div className="p-6 space-y-4">
            <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
              <div className="flex items-start gap-2">
                <AlertCircle className="h-5 w-5 text-red-600 mt-0.5 shrink-0" />
                <div className="text-sm text-red-800 space-y-1">
                  <p className="font-semibold">
                    Você está prestes a excluir permanentemente{' '}
                    <span className="font-mono">{tenantToDelete.slug}</span> e TUDO
                    relacionado a ele:
                  </p>
                  <ul className="list-disc list-inside text-xs text-red-700 space-y-0.5">
                    <li>Todos os usuários, clientes, produtos, questionários e pipelines do estúdio</li>
                    <li>Fotos, palavras-chave, respostas, visões gerais por IA e relatórios</li>
                    <li>Sessões, conversas e mensagens do WhatsApp</li>
                    <li>Histórico de assinatura, configurações de webhook e demais configurações</li>
                    <li>Todos os arquivos no S3 (logos, fotos, relatórios, produtos)</li>
                  </ul>
                </div>
              </div>
            </div>

            {error && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
                {error}
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-admin-700 mb-1">
                Digite o slug do estúdio{' '}
                <code className="font-mono text-red-700">{tenantToDelete.slug}</code> para
                confirmar
              </label>
              <input
                type="text"
                value={deleteConfirmText}
                onChange={(e) => setDeleteConfirmText(e.target.value)}
                className="input font-mono"
                placeholder={tenantToDelete.slug}
                disabled={deleting}
                autoFocus
              />
            </div>

            <div className="flex items-center justify-end space-x-3 pt-2 border-t border-admin-200">
              <button
                type="button"
                onClick={closeDeleteModal}
                disabled={deleting}
                className="btn btn-secondary"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={confirmHardDelete}
                disabled={deleting || deleteConfirmText.trim() !== tenantToDelete.slug}
                className="btn btn-danger"
              >
                {deleting ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Excluindo...
                  </>
                ) : (
                  <>
                    <Trash2 className="h-4 w-4 mr-2" />
                    Excluir permanentemente
                  </>
                )}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
